import React, { useState, useEffect, useRef } from 'react'

export const PROXY_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'WIFI/LTE' },
  { value: 'wifi', label: 'WIFI' },
  { value: 'cell', label: 'LTE' },
]

export const PROXY_OS_FILTER_OPTIONS = [
  { value: '', label: 'All OS' },
  { value: 'windows', label: 'Windows' },
  { value: 'macos', label: 'MacOS' },
  { value: 'android', label: 'Android' },
]

const FilterSelect = ({
  options = [],
  value,
  onChange,
  className = '',
  style,
  triggerStyle,
  useFixedDropdown = false,
}) => {
  const [open, setOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const containerRef = useRef(null)
  const triggerRef = useRef(null)

  const normalizedValue = value ?? ''
  const selectedOption = options.find((o) => o.value === normalizedValue) ?? options[0]

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setDropdownPosition({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    })
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (!open || !useFixedDropdown) return

    const handleReposition = () => updateDropdownPosition()

    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [open, useFixedDropdown])

  const handleToggle = () => {
    if (!open && useFixedDropdown) {
      updateDropdownPosition()
    }
    setOpen((prev) => !prev)
  }

  const handleSelect = (option) => {
    setOpen(false)
    onChange(option.value === '' ? null : option.value)
  }

  const dropdownStyle = useFixedDropdown
    ? {
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        zIndex: 9999,
      }
    : undefined

  return (
    <div
      ref={containerRef}
      className={`filter-select ${className}`.trim()}
      style={{ position: 'relative', width: '100%', ...style }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="form-input shop-filter-select shop-filter-select-trigger"
        style={triggerStyle}
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {selectedOption?.label ?? ''}
      </button>
      {open && (
        <div className="shop-filter-dropdown" role="listbox" style={dropdownStyle}>
          {options.map((option) => (
            <button
              key={option.value === '' ? '__all__' : option.value}
              type="button"
              role="option"
              aria-selected={option.value === normalizedValue}
              className={`shop-filter-dropdown__option${
                option.value === normalizedValue ? ' shop-filter-dropdown__option--selected' : ''
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default FilterSelect
